import React, { ChangeEvent, FC, Fragment } from 'react';
import { Divider, Grid, Switch, Typography, SvgIcon } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import LinkedIn from '@material-ui/icons/LinkedIn';
import Twitter from '@material-ui/icons/Twitter';
import Facebook from '@material-ui/icons/Facebook';
import YouTube from '@material-ui/icons/YouTube';
import Instagram from '@material-ui/icons/Instagram';

import classNames from 'classnames';

import { useAdminUpdateClientMutation } from 'api';
import { useProtectedClient, useNotify } from 'hooks';
import { useAdminUpdateClient } from 'features/admin/updaters';
import { Tip } from 'components';

const SOCIAL_MEDIA = ['linkedin', 'twitter', 'facebook', 'youtube', 'instagram'];

const useStyles = makeStyles(theme => ({
  divider: { margin: theme.spacing(3, 0) },
  itemContainer: { padding: theme.spacing(2, 0) },
  icon: {
    marginRight: theme.spacing(3),
  },
  twitter: { color: theme.custom.networks.twitter },
  linkedin: { color: theme.custom.networks.linkedin },
  facebook: { color: theme.custom.networks.facebook },
  youtube: { color: theme.custom.networks.youtube },
  instagram: theme.custom.networks.instagram,
}));

const PostCTAs: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const client = useProtectedClient();
  const notify = useNotify();

  const mutationOptions = useAdminUpdateClient();
  const { mutate } = useAdminUpdateClientMutation({
    onSuccess: () => {
      notify.info({ message: t('components:savedSuccessfully') });
    },
    ...mutationOptions,
  });

  function getIcon(name: string) {
    switch (name) {
      case 'linkedin':
        return (
          <SvgIcon
            component={LinkedIn}
            fontSize="large"
            className={classNames(classes.icon, classes.linkedin)}
          />
        );
      case 'twitter':
        return (
          <SvgIcon
            component={Twitter}
            fontSize="large"
            className={classNames(classes.icon, classes.twitter)}
          />
        );
      case 'facebook':
        return (
          <SvgIcon
            component={Facebook}
            fontSize="large"
            className={classNames(classes.icon, classes.facebook)}
          />
        );
      case 'youtube':
        return (
          <SvgIcon
            component={YouTube}
            fontSize="large"
            className={classNames(classes.icon, classes.youtube)}
          />
        );
      case 'instagram':
        return (
          <SvgIcon
            component={Instagram}
            fontSize="large"
            className={classNames(classes.icon, classes.instagram)}
          />
        );
      default:
        return null;
    }
  }

  function handleSwitchUpdate(event: ChangeEvent<HTMLInputElement>) {
    const { id } = client;
    const { name, checked } = event.target;

    const data = {
      id,
      social_config: {
        ...client.social_config,
        [name]: checked,
      },
    };

    return mutate({ data });
  }

  function renderSocialPosts() {
    const { social_config } = client;

    return SOCIAL_MEDIA.map((name, idx) => {
      const engageKey = `${name}_engageable`;
      const shareKey = `${name}_shareable`;
      const isEngageable = social_config[engageKey];
      const isShareable = social_config[shareKey];

      return (
        <Grid item key={idx}>
          <Grid
            container
            alignItems="flex-start"
            justify="space-between"
            className={classes.itemContainer}
          >
            <Grid item>
              <Grid container alignItems="center">
                {getIcon(name)}
                <Typography display="inline" variant="subtitle2">
                  {t(`admin:${name}`)}
                </Typography>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="column" alignItems="flex-end" spacing={3}>
                <Grid item>
                  <Typography variant="body2" display="inline" color="textSecondary">
                    {t('admin:socialPostEngage')}
                  </Typography>
                  <Switch checked={isEngageable} name={engageKey} onChange={handleSwitchUpdate} />
                </Grid>
                <Grid item>
                  <Typography variant="body2" display="inline" color="textSecondary">
                    {t('admin:socialPostShare')}
                  </Typography>
                  <Switch checked={isShareable} name={shareKey} onChange={handleSwitchUpdate} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {name !== 'instagram' && <Divider className={classes.divider} />}
        </Grid>
      );
    });
  }

  return (
    <Fragment>
      <Typography>{t('admin:socialPostHelp')}</Typography>
      <Tip i18nKey="admin:socialPostTip" />
      <Divider className={classes.divider} />

      <Grid container direction="column">
        {renderSocialPosts()}
      </Grid>
    </Fragment>
  );
};

export default PostCTAs;
